import { MoPhong } from '../../types'

// Vite glob import - load all simulation files at build time
const simulationModules = import.meta.glob('../../du-lieu/mo-phong/**/*.json', { eager: false })

console.log('📦 Available simulation modules:', Object.keys(simulationModules))

// Interface for lesson simulation index
interface LessonSimulationIndex {
  bai: number
  tieuDe: string
  moPhong: SimulationReference[]
  metadata?: any
}

interface SimulationReference {
  id: string
  file: string
  type: string
  title: string
  order: number
  section: string
}

export class SimulationService {
  private static simulationCache = new Map<string, any>()
  private static lessonIndexCache = new Map<string, LessonSimulationIndex>()
  
  /**
   * Load simulation from the new structure: lop-X/chuong-Y/bai-Z/
   */
  static async loadLessonSimulation(
    lop: number, 
    chuong: number, 
    bai: number, 
    simulationFile: string
  ): Promise<any | null> {
    const cacheKey = `${lop}-${chuong}-${bai}-${simulationFile}`
    const isDevelopment = import.meta.env.DEV
    
    // Check cache first (only in production)
    if (!isDevelopment && this.simulationCache.has(cacheKey)) {
      return this.simulationCache.get(cacheKey)!
    }
    
    try {
      const basePath = this.getLessonSimulationPath(lop, chuong, bai)
      const fullPath = `../../du-lieu/mo-phong/${basePath}/${simulationFile}`
      
      console.log(`🔍 Loading simulation via glob import: ${fullPath}`)
      
      // Find matching module from glob imports
      const moduleKey = Object.keys(simulationModules).find(key => key.endsWith(fullPath))
      
      if (!moduleKey) {
        console.error(`❌ Module not found in glob imports. Available modules:`, Object.keys(simulationModules))
        console.error(`   Looking for: ${fullPath}`)
        throw new Error(`Module not found: ${fullPath}`)
      }
      
      console.log(`✅ Found module: ${moduleKey}`)
      
      // Load the module
      const module: any = await simulationModules[moduleKey]()
      const simulation = module.default || module
      
      // Only cache in production mode
      if (!isDevelopment) {
        this.simulationCache.set(cacheKey, simulation)
      }
      
      console.log(`✅ Loaded simulation: ${simulationFile}`, simulation)
      return simulation
      
    } catch (error) {
      console.error(`❌ Failed to load simulation ${simulationFile}:`, error)
      return null
    }
  }
  
  /**
   * Load the simulation index for a lesson
   */
  static async loadLessonSimulations(
    lop: number, 
    chuong: number, 
    bai: number
  ): Promise<LessonSimulationIndex | null> {
    const cacheKey = `index-${lop}-${chuong}-${bai}`
    const isDevelopment = import.meta.env.DEV
    
    // Check cache first (only in production)
    if (!isDevelopment && this.lessonIndexCache.has(cacheKey)) {
      return this.lessonIndexCache.get(cacheKey)!
    }
    
    try {
      const basePath = this.getLessonSimulationPath(lop, chuong, bai)
      const fullPath = `../../du-lieu/mo-phong/${basePath}/index.json`
      
      console.log(`🔍 Loading simulation index via glob import: ${fullPath}`)
      
      // Find matching module from glob imports
      const moduleKey = Object.keys(simulationModules).find(key => key.endsWith(fullPath))
      
      if (!moduleKey) {
        console.error(`❌ Index module not found in glob imports`)
        console.error(`   Looking for: ${fullPath}`)
        console.error(`   Available modules:`, Object.keys(simulationModules).filter(k => k.includes(basePath)))
        throw new Error(`Index module not found: ${fullPath}`)
      }
      
      console.log(`✅ Found index module: ${moduleKey}`)
      
      // Load the module
      const module: any = await simulationModules[moduleKey]()
      const index = (module.default || module) as LessonSimulationIndex
      
      // Only cache in production mode
      if (!isDevelopment) {
        this.lessonIndexCache.set(cacheKey, index)
      }
      
      console.log(`✅ Loaded simulation index:`, index)
      return index
      
    } catch (error) {
      console.error(`❌ Failed to load simulation index for lop ${lop}, chuong ${chuong}, bai ${bai}:`, error)
      return null
    }
  }
  
  /**
   * Load all simulations for a lesson with their data
   */
  static async loadAllLessonSimulations(
    lop: number, 
    chuong: number, 
    bai: number
  ): Promise<any[]> {
    const index = await this.loadLessonSimulations(lop, chuong, bai)
    
    if (!index || !index.moPhong) {
      return []
    }
    
    const simulations = await Promise.all(
      index.moPhong.map(async (ref) => {
        const data = await this.loadLessonSimulation(lop, chuong, bai, ref.file)
        return {
          ...ref,
          data
        }
      })
    )
    
    return simulations.filter(sim => sim.data !== null)
  }
  
  /**
   * Get simulations by section (phanMoDau, phanNoiDung, phanCuoi)
   */
  static async getLessonSimulationsBySection(
    lop: number, 
    chuong: number, 
    bai: number,
    section: string
  ): Promise<any[]> {
    const allSimulations = await this.loadAllLessonSimulations(lop, chuong, bai)
    return allSimulations.filter(sim => sim.section === section)
  }
  
  /**
   * Get the path for a lesson's simulation folder
   */
  private static getLessonSimulationPath(lop: number, chuong: number, bai: number): string {
    // Map class numbers to folder names
    const classFolders: Record<number, string> = {
      10: 'lop-10',
      11: 'lop-11',
      12: 'lop-12'
    }
    
    // Map chapter numbers to folder names
    const chapterFolders: Record<number, Record<number, string>> = {
      10: {
        0: 'chuong-0-mo-dau',
        1: 'chuong-1-dong-hoc',
        2: 'chuong-2-dong-luc-hoc',
        3: 'chuong-3-nang-luong-va-cong',
        4: 'chuong-4-chuyen-dong-tron-va-bien-dang'
      },
      11: {
        1: 'chuong-1-dao-dong',
        2: 'chuong-2-song',
        3: 'chuong-3-dien-truong',
        4: 'chuong-4-dong-dien-mach-dien'
      },
      12: {
        1: 'chuong-1-vat-li-nhiet',
        2: 'chuong-2-khi-li-tuong',
        3: 'chuong-3-tu-truong',
        4: 'chuong-4-vat-li-hat-nhan'
      }
    }
    
    // Lesson name mappings - based on actual folder structure
    const lessonFolders: Record<number, Record<number, Record<number, string>>> = {
      10: {
        0: {
          1: 'bai-1-gioi-thieu-muc-dich-hoc-tap-mon-vat-li'
        },
        1: {
          2: 'bai-2-do-dich-chuyen-va-quang-duong-di-duoc',
          3: 'bai-3-toc-do-va-van-toc-chuyen-dong-tong-hop',
          4: 'bai-4-do-thi-do-dich-chuyen-thoi-gian',
          5: 'bai-5-chuyen-dong-bien-doi',
          6: 'bai-6-chuyen-dong-roi-tu-do',
          7: 'bai-7-chuyen-dong-nem'
        },
        2: {
          8: 'bai-8-ba-dinh-luat-newton-ve-chuyen-dong',
          9: 'bai-9-mot-so-luc-trong-thuc-tien',
          10: 'bai-10-tong-hop-luc-va-phan-tich-luc',
          11: 'bai-11-can-bang-luc-moment-luc',
          12: 'bai-12-khoi-luong-rieng-ap-suat-chat-long'
        },
        3: {
          13: 'bai-13-cong-va-nang-luong',
          14: 'bai-14-dong-nang-va-the-nang',
          15: 'bai-15-cong-suat-va-hieu-suat',
          16: 'bai-16-dong-luong-dinh-luat-bao-toan-dong-luong',
          17: 'bai-17-dong-luong-va-va-cham'
        },
        4: {
          18: 'bai-18-dong-hoc-chuyen-dong-tron-deu',
          19: 'bai-19-gia-toc-huong-tam-va-luc-huong-tam',
          20: 'bai-20-su-bien-dang-dinh-luat-hooke'
        }
      },
      11: {
        1: {
          1: 'bai-1-dao-dong-dieu-hoa-phuong-trinh-dao-dong-dieu-hoa',
          2: 'bai-2-van-toc-gia-toc-trong-dao-dong-dieu-hoa',
          3: 'bai-3-nang-luong-trong-dao-dong-dieu-hoa',
          4: 'bai-4-dao-dong-tat-dan-dao-dong-cuong-buc-hien-tuong-cong-huong'
        },
        2: {
          5: 'bai-5-mo-ta-song-su-truyen-song',
          6: 'bai-6-cac-dac-trung-cua-song-song-doc-va-song-ngang',
          7: 'bai-7-song-dien-tu',
          8: 'bai-8-giao-thoa-song',
          9: 'bai-9-song-dung',
          10: 'bai-10-do-toc-do-truyen-am'
        },
        3: {
          11: 'bai-11-luc-tuong-tac-giua-cac-dien-tich-dinh-luat-coulomb',
          12: 'bai-12-khai-niem-dien-truong-dien-truong-deu',
          13: 'bai-13-dien-the-va-the-nang-dien',
          14: 'bai-14-dien-dung-va-tu-dien-nang-luong-dien-truong'
        },
        4: {
          15: 'bai-15-dong-dien-cuong-do-dong-dien',
          16: 'bai-16-dien-tro-dinh-luat-ohm',
          17: 'bai-17-nguon-dien',
          18: 'bai-18-nang-luong-dien-cong-suat-dien'
        }
      },
      12: {
        1: {
          1: 'bai-1-su-chuyen-the',
          2: 'bai-2-noi-nang-dinh-luat-1-cua-nhiet-dong-luc-hoc',
          3: 'bai-3-thang-nhiet-do-nhiet-ke',
          4: 'bai-4-nhiet-dung-rieng',
          5: 'bai-5-nhiet-nong-chay-rieng',
          6: 'bai-6-nhiet-hoa-hoi-rieng'
        },
        2: {
          7: 'bai-7-mo-hinh-dong-hoc-phan-tu-chat-khi',
          8: 'bai-8-dinh-luat-boyle',
          9: 'bai-9-dinh-luat-charles',
          10: 'bai-10-phuong-trinh-trang-thai-cua-khi-li-tuong',
          11: 'bai-11-ap-suat-theo-mo-hinh-dong-hoc-phan-tu-dong-nang-phan-tu'
        },
        3: {
          12: 'bai-12-tu-truong',
          13: 'bai-13-luc-tu-tac-dung-len-doan-day-dan-mang-dong-dien-cam-ung-tu',
          14: 'bai-14-tu-thong-hien-tuong-cam-ung-dien-tu',
          15: 'bai-15-may-phat-dien-xoay-chieu-dai-cuong-ve-dong-dien-xoay-chieu',
          16: 'bai-16-ung-dung-cua-hien-tuong-cam-ung-dien-tu',
          17: 'bai-17-dien-tu-truong-mo-hinh-song-dien-tu'
        }
      }
    }
    
    const classFolder = classFolders[lop]
    const chapterFolder = chapterFolders[lop]?.[chuong]
    const lessonFolder = lessonFolders[lop]?.[chuong]?.[bai]
    
    if (!classFolder || !chapterFolder) {
      throw new Error(`No folder mapping found for lop ${lop}, chuong ${chuong}`)
    }
    
    if (!lessonFolder) {
      throw new Error(`No lesson folder mapping found for lop ${lop}, chuong ${chuong}, bai ${bai}`)
    }
    
    return `${classFolder}/${chapterFolder}/${lessonFolder}`
  }
  
  /**
   * Legacy method: Load simulation from old structure (backwards compatibility)
   */
  static async loadSimulation(simulationId: string): Promise<MoPhong | null> {
    // In development mode, skip cache to allow hot-reloading of content changes
    const isDevelopment = import.meta.env.DEV
    
    // Check cache first (only in production)
    if (!isDevelopment && this.simulationCache.has(simulationId)) {
      return this.simulationCache.get(simulationId)!
    }
    
    try {
      // Load simulation data from JSON files (old structure)
      const simulationModule = await import(`../../du-lieu/mo-phong/${simulationId}.json`)
      
      if (simulationModule && simulationModule.default) {
        const simulation = simulationModule.default as MoPhong
        
        // Only cache in production mode
        if (!isDevelopment) {
          this.simulationCache.set(simulationId, simulation)
        }
        
        return simulation
      }
      
      return null
    } catch (error) {
      console.error(`Failed to load simulation ${simulationId}:`, error)
      return this.getMockSimulation(simulationId)
    }
  }
  
  static async getAvailableSimulations(): Promise<MoPhong[]> {
    // Return list of available simulations
    const simulationIds = [
      'chuyen-dong-thang',
      'chuyen-dong-tron',
      'dao-dong-dieu-hoa',
      'song-co-hoc',
      'dien-truong',
      'tu-truong',
      'phan-hach-hat-nhan'
    ]
    
    const simulations: MoPhong[] = []
    
    for (const id of simulationIds) {
      const simulation = await this.loadSimulation(id)
      if (simulation) {
        simulations.push(simulation)
      }
    }
    
    return simulations
  }
  
  static getSimulationsByTopic(topic: string): Promise<MoPhong[]> {
    // Filter simulations by topic/subject
    return this.getAvailableSimulations().then(simulations => 
      simulations.filter(sim => 
        sim.vat_ly && sim.vat_ly.don_vi && sim.vat_ly.don_vi.includes(topic)
      )
    )
  }
  
  private static getMockSimulation(simulationId: string): MoPhong {
    return {
      id: simulationId,
      tieu_de: `Mô phỏng ${simulationId} - Đang cập nhật`,
      loai: 'physics_simulation',
      mo_ta: 'Mô phỏng đang được phát triển. Vui lòng quay lại sau.',
      cau_hinh: {
        canvas: {
          chieu_rong: 800,
          chieu_cao: 600,
          mau_nen: '#f0f0f0'
        },
        doi_tuong: [
          {
            loai: 'placeholder',
            vi_tri_ban_dau: { x: 400, y: 300 },
            mau: '#cccccc',
            kich_thuoc: 50,
            nhan: 'Đang cập nhật'
          }
        ],
        dieu_khien: [
          {
            loai: 'button',
            nhan: 'Bắt đầu',
            hanh_dong: 'start_simulation'
          }
        ],
        do_luong: [
          {
            thuoc_tinh: 'status',
            cong_thuc: 'simulation_status',
            hien_thi: 'Trạng thái: Đang cập nhật'
          }
        ]
      },
      vat_ly: {
        buoc_thoi_gian: 0.016,
        thoi_gian_thuc: true,
        don_vi: 'SI'
      }
    }
  }
  
  static clearCache(): void {
    this.simulationCache.clear()
  }
}